/**
 * Trusted parent-window origins for the iframe postMessage bridge.
 *
 * The shell (parent) renders this app inside an iframe and exchanges
 * security-sensitive messages (setAccessToken inbound, navigation and
 * signout signals outbound). We must validate the origin of every
 * incoming message and scope every outgoing message to a specific
 * origin so that a malicious parent page cannot inject a forged
 * bearer token or receive leaked signals.
 *
 * Configured via the comma-separated `SHELL_ORIGIN` env var, injected
 * at build time through webpack's DefinePlugin. When this app itself
 * is served from localhost, any localhost parent is also allowed so
 * developers can run the shell on any port. Production builds fail
 * closed (empty allowlist) when the env var is missing.
 */

// This app can run against many backends in dev (development, local_to_systest,
// local_to_playground_2, etc.), so we can't key dev mode off a single env value.
// The reliable signal is that our own document is served from localhost — in
// that case we can trust a localhost parent.
const isLocalHostedApp = (): boolean =>
	window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const parseConfiguredOrigins = (raw: string | undefined): string[] => {
	if (!raw) return [];
	return raw
		.split(',')
		.map(origin => origin.trim())
		.filter(origin => origin.length > 0);
};

const isLocalhostOrigin = (origin: string): boolean =>
	/^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(origin);

export const getConfiguredTrustedOrigins = (): string[] =>
	parseConfiguredOrigins(process.env.SHELL_ORIGIN);

export const isTrustedParentOrigin = (origin: string): boolean => {
	if (getConfiguredTrustedOrigins().includes(origin)) return true;
	if (isLocalHostedApp() && isLocalhostOrigin(origin)) return true;
	return false;
};

// Regex rather than `new URL(url).origin` because the legacy jest setup
// (`src/tests/config/setupTestsAfterEnv.ts`) stubs `window.URL` as a plain
// object without a constructor. Safe for allowlist comparison — malformed
// inputs return null instead of matching. Follow-up will kill the stub and
// collapse onto `new URL(url).origin` to match shell's `getMicroAppOrigin`.
const extractOrigin = (url: string): string | null => {
	const match = url.match(/^https?:\/\/[^/?#]+/);
	return match ? match[0] : null;
};

/**
 * Determines the parent window's origin when this app is embedded in an
 * iframe. Returns null when the app is not embedded or the parent origin
 * cannot be determined.
 *
 * `window.location.ancestorOrigins` is Chromium/WebKit only — Firefox
 * always falls through to the `document.referrer` branch. That branch
 * relies on the shell NOT setting a stricter-than-default `Referrer-Policy`:
 * the browser default (`strict-origin-when-cross-origin`) sends the parent
 * origin in the referrer, which is sufficient. If the shell later sets
 * `Referrer-Policy: no-referrer` / `same-origin`, or sandboxes the iframe
 * without `allow-same-origin`, `document.referrer` is empty and this
 * function returns null — the outbound bridge then fails closed (logged
 * once per session by `CommunicationBridgeProvider`).
 */
export const detectParentOrigin = (): string | null => {
	const ancestors = window.location.ancestorOrigins;
	if (ancestors && ancestors.length > 0) {
		return ancestors[0] ?? null;
	}
	if (document.referrer) {
		return extractOrigin(document.referrer);
	}
	return null;
};

/**
 * Resolves the targetOrigin to use for outbound postMessage calls.
 * Returns the parent's origin only if it is in the trusted allowlist,
 * otherwise null (caller must not post). Never returns "*".
 */
export const resolveTrustedParentTargetOrigin = (): string | null => {
	const parentOrigin = detectParentOrigin();
	if (!parentOrigin) return null;
	return isTrustedParentOrigin(parentOrigin) ? parentOrigin : null;
};
